import { inject, Injectable, Injector } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { ChainsStore } from '@services/chains.store';
import { EtlRoutesStore } from '@services/etl-routes.store';
import { FeatureFlagsService } from '@services/feature-flags.service';
import { PharmaciesSearchStore } from '@services/pharmacies.search.store';
import { PharmaciesSimpleSearchStore } from '@services/pharmacies.simple.search.store';
import { ProductsStore } from '@services/products.store';
import { ProgramGroupsStore } from '@services/program-groups.store';
import { SettingsStore } from '@services/settings.store';
import { VersionService } from '@services/version.service';

@Injectable({ providedIn: 'root' })
export class AppService {
  private readonly versionService = inject(VersionService);
  private readonly injector = inject(Injector);
  readonly stores = {
    chains: inject(ChainsStore),
    etlRoutes: inject(EtlRoutesStore),
    pharmacies: inject(PharmaciesSearchStore),
    pharmaciesSimple: inject(PharmaciesSimpleSearchStore),
    products: inject(ProductsStore),
    programGroups: inject(ProgramGroupsStore),
    settings: inject(SettingsStore),
  };

  async loadFeatureFlags() {
    await this.injector.get(FeatureFlagsService).reload();
  }

  checkVersion(): Promise<boolean> {
    return this.versionService.checkVersion(() => this.clearStores());
  }

  async clearStores() {
    const results = await Promise.allSettled(
      Object.entries(this.stores).map(([name, store]) => store.reset().catch(err => Promise.reject({ name, err }))),
    );
    const failures = results
      .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
      .map(({ reason }) => reason as { name: string; err: unknown });
    if (!failures.length) return;

    // IndexedDB itself can be unavailable/corrupted on the device (e.g. locked-down kiosk storage) —
    // that's an environment issue we can't fix in-app, so don't spam Sentry/replay with it per store.
    const storageFailures = failures.filter(({ err }) => err instanceof DOMException);
    if (storageFailures.length) {
      Sentry.addBreadcrumb({
        category: 'store',
        level: 'warning',
        message: `IndexedDB reset failed: ${storageFailures.map(({ name }) => name).join(', ')}`,
      });
    }
    failures
      .filter(({ err }) => !(err instanceof DOMException))
      .forEach(({ name, err }) => Sentry.captureException(err, { extra: { store: name } }));
  }

  async refreshStores() {
    await Promise.all(Object.values(this.stores).map(store => store.refresh()));
  }
}
